import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
	output,
	signal,
	ViewEncapsulation,
} from '@angular/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { matDateProviders } from '../../../../../shared/helpers/date.func';
import { DatabaseDateFormatService } from '../../services/database-date-format.service';

export interface DatabaseDateRangeValue {
	start: Date | null;
	end: Date | null;
}

@Component({
	selector: 'lvr-database-date-range-filter',
	imports: [MatDatepickerModule, MatIconButton, MatIcon, TranslocoPipe],
	providers: [matDateProviders],
	templateUrl: './database-date-range-filter.html',
	styleUrl: './database-date-range-filter.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		style: 'display: contents;',
	},
	encapsulation: ViewEncapsulation.None,
})
export class DatabaseDateRangeFilter {
	private readonly dateFormat = inject(DatabaseDateFormatService);

	public readonly start = input<Date | null>(null);
	public readonly end = input<Date | null>(null);
	public readonly disabled = input(false);
	public readonly labelKey = input.required<string>();
	public readonly rangeChanged = output<DatabaseDateRangeValue>();
	public readonly rangeCleared = output<void>();

	protected readonly invalid = signal(false);
	protected readonly hasValue = computed(() => !!this.start() || !!this.end());
	protected readonly displayValue = computed(() => this.dateFormat.formatPickerRange(this.start(), this.end()));

	protected onDateInput(bound: 'start' | 'end', value: Date | null): void {
		const next: DatabaseDateRangeValue = {
			start: bound === 'start' ? value : this.start(),
			end: bound === 'end' ? value : this.end(),
		};
		if (next.start && next.end && next.start > next.end) {
			this.invalid.set(true);
			return;
		}
		this.invalid.set(false);
		this.rangeChanged.emit(next);
	}

	protected clear(event: Event): void {
		event.stopPropagation();
		this.invalid.set(false);
		this.rangeCleared.emit();
	}
}
